/* eslint-disable jsx-a11y/aria-role */
/* eslint-disable react-hooks/exhaustive-deps */

import _, { forEach } from 'underscore';
import React, { useState, memo, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { RadioGroup } from '@bingads-webui-react/radio';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Glyphicon } from '@bingads-webui-react/primitive';

import { Scopes, ScopeOptions, GoalCategories, TrackingStatusMapping } from './config/constants';
import { GoalSelector } from './goal-selector';
import { getStyles } from './style/styles';

function GoalScopeSelector(props) {
  const {
    campaignConversionGoal,
    i18n,
    classes,
    onScopeChange,
    turnOnRemoveAlert,
    activityLogger,
    subTextOptions,
    showTrackingStatusIcon,
  } = props;

  const renderHeaderFactory = ({ isSelected }) => function renderHeader(itemCount) {
    return (
      <div className={`multi-item-container ${classes.rowItem}`}>
        <div className={`goal-selector-header-name ${classes.goalNameColumn}`}>
          <b>
            {i18n.getString(isSelected ? 'GoalSelector_Result_List_Header' :'GoalSelector_List_Header', { itemCount })}
          </b>
        </div>
        <div className={`goal-selector-header-type ${classes.goalTypeColumn}`}>
          <b>
            {i18n.getString('UnifiedEventTracking_Goal_Creation_Type_Event_Category')}
          </b>
        </div>
      </div>
    );
  };

  const TrackingStatusIcon = ({ item, isSelected }) => {
    if (!item.TrackingStatus && TrackingStatusMapping[item.TrackingStatus] && item.Type) {
      return <></>;
    }

    const iconState = !isSelected && item.isAddedToResultList;
    const isOfflineGoal = item.Type === 'OfflineConversionGoal';
    const uetTagGlyphIcon = (item.TrackingStatus !== 'RecordingConversions' && !isOfflineGoal) && !iconState ? 'ba-Warning' : '';
    return (<Glyphicon
      title={i18n.getString(TrackingStatusMapping[item.TrackingStatus].i18nKey)}
      className={`goal-selector-trackingStatus ${classes.trackingStatusIcon} ${iconState}`}
      glyph={uetTagGlyphIcon}
      iconStyle="error"
    />);
  };

  const renderItemFactory = ({ isSelected }) => function renderItem(item, index, forwardedRef) {
    const { isAddedToResultList } = item;
    const rowItemClass = isAddedToResultList ? 'added-to-result-list' : '';
    const addIconClass = isAddedToResultList ? 'disabled' : 'enabled';
    const categoryI18nKey = GoalCategories[item.GoalCategory] ?
      GoalCategories[item.GoalCategory].i18nKey
      : GoalCategories.None.i18nKey;

    return (
      <div className={`multi-item-container ${rowItemClass} ${classes.rowItem}`}>
        <div className={`goal-selector-name ${classes.goalNameColumn}`} ref={forwardedRef}>
          {item.Name}
        </div>
        <div className={`goal-selector-type ${classes.goalTypeColumn}`}>
          {i18n.getString(categoryI18nKey)}
        </div>
        { showTrackingStatusIcon && <TrackingStatusIcon
          item={item}
          isSelected={isSelected}
        />}
        {isSelected ?
          <span className={`arrow-button a11y-k-listview-button remove remove-arrow-button ${classes.arrowIcon}`} /> :
          <span className={`arrow-button a11y-k-listview-button add-arrow-button ${addIconClass} ${classes.arrowIcon}`} />
        }
      </div>
    );
  };

  const hasCampaignLevelGoals = campaignConversionGoal && campaignConversionGoal.Goals
    && campaignConversionGoal.Goals.length > 0;

  const [selectedScope, setSelectedScope] = useState(hasCampaignLevelGoals ?
    Scopes.CAMPAIGN : Scopes.ACCOUNT);

  const onRadioChange = (item) => {
    if (activityLogger) {
      activityLogger.signal(`goal scope change: ${item}`);
    }
    if (item === Scopes.ACCOUNT) {
      turnOnRemoveAlert(false);
    }
    setSelectedScope(item);
  };

  const onGlobalShortcut = useCallback((e) => {
    e.preventDefault();
    if (_.isElement(document.getElementsByClassName('conversions')[0]) && _.isElement(document.getElementsByClassName('conversions')[0].getElementsByClassName('content-show')[0])) {
      if ((e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'Tab') && document.activeElement.type === 'radio') {
        forEach(document.getElementsByClassName('goal-selector-with-scope')[0].getElementsByTagName('Label'), (node) => {
          node.setAttribute('class', node.getElementsByTagName('input')[0].checked ? 'focusStyle' : '');
        });
      }
      if (e.type === 'mouseup' || document.activeElement.type !== 'radio') {
        forEach(document.getElementsByClassName('goal-selector-with-scope')[0].getElementsByTagName('Label'), (node) => {
          node.setAttribute('class', '');
        });
      }
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keyup', onGlobalShortcut);
    document.addEventListener('mouseup', onGlobalShortcut);
    return () => {
      document.removeEventListener('keyup', onGlobalShortcut);
      document.removeEventListener('mouseup', onGlobalShortcut);
    };
  }, [i18n, onGlobalShortcut]);

  useEffect(() => {
    setSelectedScope(hasCampaignLevelGoals ?
      Scopes.CAMPAIGN : Scopes.ACCOUNT);
  }, [hasCampaignLevelGoals]);

  useEffect(() => {
    // Update Scope when loading
    if (!_.isUndefined(onScopeChange)) onScopeChange(selectedScope);
  }, [selectedScope]);

  const renderScopeOptions = (option) => {
    const optionText = i18n.getString(_.result(_.findWhere(ScopeOptions, { id: option }), 'text'));
    const subText = subTextOptions && i18n.getString(_.result(_.findWhere(subTextOptions, { id: option }), 'text'));
    return (
      <>
        <span>{optionText}</span>
        {subText && <div className="goal-selector-subtext">{subText}</div>}
      </>
    );
  };

  const newProps = _.defaults({
    sourceGoalSelectorOptions: {
      renderHeader: renderHeaderFactory({ isSelected: false }),
      renderItem: renderItemFactory({ isSelected: false }),
      onSelect: () => { turnOnRemoveAlert(true); },
      onSelectAll: () => { turnOnRemoveAlert(true); },
    },
    resultContainerOptions: {
      isHideSaveBar: true,
      renderHeader: renderHeaderFactory({ isSelected: true }),
      renderItem: renderItemFactory({ isSelected: true }),
      onRemove: () => { turnOnRemoveAlert(true); },
      onRemoveAll: () => { turnOnRemoveAlert(true); },
    },
  }, props);

  return (
    <div className={`goal-selector-with-scope ${classes.goalSelectorContainer}`}>
      <div role="radioGroup" aria-label={i18n.getString('GoalSelector_Radio_Aria_Label')}>
        <RadioGroup
          items={_.pluck(ScopeOptions, 'id')}
          name="conversion-goal-options"
          onChange={onRadioChange}
          checked={selectedScope}
          renderItem={renderScopeOptions}
        />
      </div>
      {
          selectedScope === Scopes.CAMPAIGN && (
          <div className={`goal-selector ${classes.goalSelector}`}>
            <GoalSelector {...newProps} />
          </div>
        )
      }
    </div>
  );
}

GoalScopeSelector.propTypes = {
  classes: PropTypes.shape({
    goalSelectorContainer: PropTypes.string,
    goalSelector: PropTypes.string,
    rowItem: PropTypes.string,
    goalNameColumn: PropTypes.string,
    goalTypeColumn: PropTypes.string,
    arrowIcon: PropTypes.string,
  }),
  campaignConversionGoal: PropTypes.shape({
    IsConversionGoalSet: PropTypes.bool,
    Goals: PropTypes.arrayOf(PropTypes.shape({
      Id: PropTypes.number,
      Name: PropTypes.string,
    })),
  }),
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  handleErrorFunc: PropTypes.func,
  onScopeChange: PropTypes.func.isRequired,
  turnOnRemoveAlert: PropTypes.func,
  activityLogger: PropTypes.shape({
    signal: PropTypes.func,
  }),
  showTrackingStatusIcon: PropTypes.bool,
  subTextOptions: PropTypes.shape({
    id: PropTypes.string,
    text: PropTypes.string,
  }),
};

GoalScopeSelector.defaultProps = {
  showTrackingStatusIcon: false,
  handleErrorFunc: _.noop,
  turnOnRemoveAlert: _.noop,
  campaignConversionGoal: {
    IsConversionGoalSet: false,
    Goals: [],
  },
  classes: {},
  subTextOptions: null,
  activityLogger: null,
};

const Wrapped = _.compose(
  memo,
  Comp => withDefaultStyles(Comp, getStyles)
)(GoalScopeSelector);

export { Wrapped as CampaignGoalLevelEditor };
